import { Plus } from "lucide-react";
import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import type { WholesaleCustomerView } from "../../../shared/contracts.js";
import { flattenPages } from "../../api/caseQueries.js";
import { useCustomerList } from "../../api/wholesaleQueries.js";
import { Button } from "../../ui/Button.js";
import { FormField } from "../../ui/FormField.js";
import { CustomerDialog } from "./CustomerDialog.js";

/**
 * The customer type-ahead of the wholesale order form (spec §4.3): a
 * combobox over the *active* customers by name, with the directory form
 * inline behind **Add customer** — it returns with the new customer
 * selected, so the desk never leaves the order to add one.
 *
 * Typing asks `GET /api/wholesale/customers?q=` after a short pause and
 * lists the matches under the field (arrow keys, Enter, Escape or a
 * click); choosing one fills the field with the name and reports the id.
 * Editing the text again drops the selection until another is chosen —
 * the order is never sent with a name that was only typed. A deactivated
 * customer is never listed, so it cannot be picked (spec §3.1).
 */

export const CUSTOMER_SEARCH_DEBOUNCE_MS = 200;

export interface CustomerPickerProps {
  id: string;
  /** The chosen customer, or `null` while nothing is selected. */
  value: WholesaleCustomerView | null;
  onChange: (customer: WholesaleCustomerView | null) => void;
  error?: string | null;
  disabled?: boolean;
}

export function CustomerPicker({ id, value, onChange, error, disabled = false }: CustomerPickerProps) {
  const [text, setText] = useState(value?.name ?? "");
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [adding, setAdding] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const listboxId = useId();

  // A selection made elsewhere (the inline dialog) shows its name.
  useEffect(() => {
    if (value) setText(value.name);
  }, [value]);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const customers = useCustomerList({ q: query }, { enabled: open && query.trim().length > 0 });
  const items = open && query.trim().length > 0 ? flattenPages(customers.data).filter((customer) => customer.active) : [];
  const listOpen = items.length > 0;

  const choose = (customer: WholesaleCustomerView) => {
    onChange(customer);
    setText(customer.name);
    setOpen(false);
    setActiveIndex(-1);
  };

  const onTextChange = (next: string) => {
    setText(next);
    setActiveIndex(-1);
    if (value && next !== value.name) onChange(null);
    setOpen(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setQuery(next.trim()), CUSTOMER_SEARCH_DEBOUNCE_MS);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape" && open) {
      event.preventDefault();
      setOpen(false);
      setActiveIndex(-1);
      return;
    }
    if (event.key === "Enter") {
      // Enter never submits the order from the picker: it chooses, or does nothing.
      event.preventDefault();
      const entry = listOpen && activeIndex >= 0 ? items[activeIndex] : undefined;
      if (entry) choose(entry);
      return;
    }
    if (!listOpen) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((current) => (current + 1) % items.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((current) => (current <= 0 ? items.length - 1 : current - 1));
    }
  };

  return (
    <div className="customer-picker">
      <FormField
        id={id}
        label="Customer"
        hint={
          value
            ? [value.contactName, value.phone, value.email ?? "No email on file"].filter(Boolean).join(" · ")
            : "Type the business name and choose from the list."
        }
        error={error ?? null}
        className="order-form__field--wide"
      >
        {(control) => (
          <div className="customer-picker__field">
            <input
              {...control}
              type="text"
              className="text-input customer-picker__input"
              data-size="desktop"
              role="combobox"
              aria-autocomplete="list"
              aria-expanded={listOpen}
              aria-controls={listOpen ? listboxId : undefined}
              aria-activedescendant={listOpen && activeIndex >= 0 ? `${listboxId}-${activeIndex}` : undefined}
              autoComplete="off"
              spellCheck={false}
              value={text}
              disabled={disabled}
              onChange={(event) => onTextChange(event.target.value)}
              onFocus={() => setOpen(true)}
              onBlur={() => {
                setOpen(false);
                setActiveIndex(-1);
              }}
              onKeyDown={onKeyDown}
            />
            {listOpen ? (
              <ul id={listboxId} role="listbox" aria-label="Customers" className="part-line__suggestions customer-picker__list">
                {items.map((customer, index) => (
                  <li
                    key={customer.id}
                    id={`${listboxId}-${index}`}
                    role="option"
                    aria-selected={index === activeIndex}
                    className="part-line__suggestion"
                    data-active={index === activeIndex ? "true" : undefined}
                    // Mouse-down would blur the input (closing the list) before the click lands.
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => choose(customer)}
                  >
                    <span className="part-line__suggestion-number">{customer.name}</span>
                    <span className="part-line__suggestion-description">
                      {[customer.contactName, customer.phone].filter(Boolean).join(" · ")}
                    </span>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        )}
      </FormField>
      <div className="customer-picker__add">
        <Button variant="secondary" icon={<Plus />} onClick={() => setAdding(true)} disabled={disabled}>
          Add customer
        </Button>
      </div>
      <CustomerDialog open={adding} onClose={() => setAdding(false)} onSaved={choose} />
    </div>
  );
}
